import React, { useState, useEffect, useRef } from "react";
import "./step3.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import emailjs from "emailjs-com";

const defaultIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// ✅ Safe Nominatim fetch wrapper (fixes mobile errors)
async function nominatimFetch(url) {
  try {
    const res = await fetch(url, {
      headers: {
        "User-Agent":
          "Mozilla/5.0 (compatible; SolarQuoteApp/1.0; +http://example.com)",
        "Accept-Language": "en",
        Referer: window.location.origin,
      },
    });

    if (!res.ok) {
      console.error("Nominatim error:", res.status, res.statusText);
      return null;
    }

    return await res.json();
  } catch (error) {
    console.error("Fetch failed:", error);
    return null;
  }
}

// Small helper to recenter map
function RecenterMap({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position);
  }, [position]);
  return null;
}

// Click & drag handler
function MapInteractions({ setPosition, setPropertyAddress }) {
  const map = useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      setPosition([lat, lng]);
      await updateAddress(lat, lng, setPropertyAddress);
    },
    touchend: async (e) => {
      if (e.latlng) {
        const { lat, lng } = e.latlng;
        setPosition([lat, lng]);
        await updateAddress(lat, lng, setPropertyAddress);
      }
    },
  });

  return null;
}

async function updateAddress(lat, lng, setPropertyAddress) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`;
  const data = await nominatimFetch(url);
  if (data?.display_name) setPropertyAddress(data.display_name);
}

export default function Step3({ panels, battery, evCharger, estimate }) {
  const [propertyAddress, setPropertyAddress] = useState("");
  const [position, setPosition] = useState([52.9254506, -1.1951394]);
  const markerRef = useRef(null);

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [postcode, setPostcode] = useState("");
  const [step, setStep] = useState(3);
  const [showStep3, setShowStep3] = useState(false);
  // Auto-geocode when address text changes
  useEffect(() => {
    if (!propertyAddress) return;

    const delay = setTimeout(async () => {
      const results = await nominatimFetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          propertyAddress
        )}`
      );

      if (results?.length > 0) {
        const { lat, lon } = results[0];
        setPosition([parseFloat(lat), parseFloat(lon)]);
      }
    }, 700);

    return () => clearTimeout(delay);
  }, [propertyAddress]);

  // When marker dragged
  const onMarkerDragEnd = async () => {
    const marker = markerRef.current;
    if (!marker) return;

    const latlng = marker.getLatLng();
    setPosition([latlng.lat, latlng.lng]);

    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}`;
    const data = await nominatimFetch(url);

    if (data?.display_name) {
      setPropertyAddress(data.display_name);
    }
  };
  const sendEmail = (e) => {
    e.preventDefault();

    const templateParams = {
      panels: panels?.toString() || "0",
      battery: battery || "N/A",
      evCharger: evCharger || "N/A",
      estimate: estimate?.toString() || "0",
      property_address: propertyAddress || "N/A",
      latitude: position?.[0]?.toString() || "0",
      longitude: position?.[1]?.toString() || "0",
      first_name: firstName || "N/A",
      last_name: lastName || "N/A",
      email: email || "N/A",
      phone: phone || "N/A",
      street: street || "N/A",
      city: city || "N/A",
      county: county || "N/A",
      postcode: postcode || "N/A",
    };

    emailjs
      .send(
        "service_7m5yclj",
        "template_m667ev8",
        templateParams,
        "gEBe2ZowUwsb4bmMq"
      )
      .then((response) => {
        alert("Quote request sent successfully!");
        console.log("SUCCESS!", response.status, response.text);
      })
      .catch((err) => {
        alert("Failed to send quote request.");
        console.error("FAILED...", err);
      });
  };

  return (
    <div className="quote-container">
      {/* Step Indicator */}
      <div className="step-navigation">
        <div className="step-info">
          Step {step} of 3
        </div>
        <div className="dots">
         <span
  className={step === 1 ? 'active' : step > 1 ? 'completed' : ''}
  onClick={() => {
    setStep(1);      // tell App.jsx to show Step 1
    setShowStep3(false); // hide Step3 page
    window.location.reload(); // refresh the page
  }} 
  style={{
    backgroundImage: step === 1 ? `url('https://electroenergy.co.uk/wp-content/uploads/2025/11/Step-base.png')` : 'none',
    backgroundSize: '28px', 
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center'
  }}
></span>
<span
  className={step === 2 ? 'active' : step > 2 ? 'completed' : ''}
  onClick={() => {
    setStep(2);
    window.location.reload(); // refresh the page
  }}
  style={{
    backgroundImage: step === 2 ? `url('https://electroenergy.co.uk/wp-content/uploads/2025/11/Step-base.png')` : 'none', 
    backgroundSize: '28px', 
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center'
  }}
></span>
          <span
            className={step === 3 ? 'active' : step > 3 ? 'completed' : ''}
            onClick={() => setStep(3)}
            style={{
              backgroundImage: step === 3 ? `url('https://electroenergy.co.uk/wp-content/uploads/2025/11/Step-base.png')` : 'none', 
              backgroundSize: '28px', 
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center'
            }}
          ></span>
        </div>
      </div>

      {/* Heading */}
      <h2 className="main-heading">
        Get Your Tailored <span className="highlight">Quote</span>
      </h2>
      <p className="subheading">
        Tell us a little about yourself and your property so we can create a
        personalized solar system quote just for you.
      </p>

      {/* Main Grid */}
      <div className="content-grid">
        {/* Left Side */}
        <div className="map-section">
          <h3 className="section-title">
            Select Your <span className="section-title-highlight">Roof</span>{" "}
          </h3>
          <p className="section-description">
            Enter your property address to select your roof and pinpoint your
            exact location.
          </p>

          <label htmlFor="property-address" className="address-label">
            Enter Your Property Address
          </label>
          <input
            id="property-address"
            type="text"
            placeholder="Search by address to quickly locate your roof"
            className="address-input"
            value={propertyAddress}
            onChange={(e) => setPropertyAddress(e.target.value)}
          />

          {/* Leaflet Map */}
        <div className="map-placeholder">
          <MapContainer
  center={position}
  zoom={17}
  className="leaflet-map"
  tap={true}
  touchZoom={true}
  dragging={true}
>


              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              <MapInteractions
                setPosition={setPosition}
                setPropertyAddress={setPropertyAddress}
              />

              <Marker
                draggable={true}
                ref={markerRef}
                position={position}
                eventHandlers={{ dragend: onMarkerDragEnd }}
              >
                <Popup>
                  {propertyAddress || "Selected Location"}
                </Popup>
              </Marker>

              <RecenterMap position={position} />
            </MapContainer>
          </div>
        </div>




        {/* Right Side Form */}
        <div className="form-section">
          <h3 className="section-title">
            Your <span style={{ color: "#FFA100" }}>Information</span>
          </h3>

          {/* FIRST + LAST NAME */}
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                className="form-input"
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input
                className="form-input"
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          {/* EMAIL */}
          <div className="form-group">
            <label>Email Address *</label>
            <input
              className="form-input"
              placeholder="Enter your Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* PHONE */}
          <div className="form-group">
            <label>Phone Number *</label>
            <input
              className="form-input"
              placeholder="Enter your Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {/* ADDRESS */}
          <div className="form-group">
            <label>Property Address *</label>
            <input
              className="form-input"
              placeholder="Street Address..."
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
          </div>

          {/* CITY + POSTCODE */}
          <div className="form-row">
            <div className="form-group">
              <input
                className="form-input"
                placeholder="City..."
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            <div className="form-group">
              <input
                className="form-input"
                placeholder="Postcode..."
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
              />
            </div>
          </div>

          {/* BUTTON */}
          <button onClick={sendEmail} className="submit-btn">
            Request My Quote →
          </button>
        </div>
      </div>




      {/* Timeline Section */}
      <div className="container">
        {/* Heading */}
        <h2 className="main-heading">
          How it <span className="highlight">Works</span>
        </h2>
        <p className="subheading">
          Simple, transparent steps to get your solar system installed quickly
          and confidently.
        </p>
        <div className="timeline-wrapper">
          <div className="vertical-line"></div>

          {/* 01 */}
          <div className="timeline-row">
            <div className="center-icon">
              <img
                src="https://electroenergy.co.uk/wp-content/uploads/2025/10/Group-3-1.png"
                alt="Step Icon"
              />
            </div>
            <div className="content-left">
              <div className="background-number">01</div>
              <div className="content-box">
                <div className="content-title">Instant Estimate</div>
                <div className="content-description">
                  Use our quick online calculator to see how much you can save
                  on energy bills and get a clear snapshot in just a few
                  seconds.
                </div>
              </div>
            </div>
          </div>

          {/* 02 */}
          <div className="timeline-row">
            <div className="center-icon">
              <img
                src="https://electroenergy.co.uk/wp-content/uploads/2025/10/Frame-1.png"
                alt="Step Icon"
              />
            </div>
            <div className="content-right">
              <div className="background-number">02</div>
              <div className="content-box">
                <div className="content-title">Site Survey</div>
                <div className="content-description">
                  We'll arrange a site visit to assess your property, confirm
                  the best system design and answer any questions you may have.
                </div>
              </div>
            </div>
          </div>

          {/* 03 */}
          <div className="timeline-row">
            <div className="center-icon">
              <img
                style={{ marginLeft: "7px" }}
                src="https://electroenergy.co.uk/wp-content/uploads/2025/10/Group-2-1.png"
                alt="Step Icon"
              />
            </div>
            <div className="content-left">
              <div className="background-number">03</div>
              <div className="content-box">
                <div className="content-title">Custom Quote</div>
                <div className="content-description">
                  Based on your site visit and details, we'll prepare a tailored
                  quote designed around your unique energy needs.
                </div>
              </div>
            </div>
          </div>

          {/* 04 */}
          <div className="timeline-row">
            <div className="center-icon">
              <img
                src="https://electroenergy.co.uk/wp-content/uploads/2025/10/Group-1-1.png"
                alt="Step Icon"
              />
            </div>
            <div className="content-right">
              <div className="background-number">04</div>
              <div className="content-box">
                <div className="content-title">Flexible Plans</div>
                <div className="content-description">
                  Select from flexible finance and payment options that suit
                  your budget and make the switch to solar easier and more
                  affordable.
                </div>
              </div>
            </div>
          </div>

          {/* 05 */}
          <div className="timeline-row">
            <div className="center-icon">
              <img
                src="https://electroenergy.co.uk/wp-content/uploads/2025/10/Group-7.png"
                alt="Step Icon"
              />
            </div>
            <div className="content-left">
              <div className="background-number">05</div>
              <div className="content-box">
                <div className="content-title">Pro Installation</div>
                <div className="content-description">
                  Relax while our accredited installers deliver a safe,
                  high-quality installation that's built to perform and last for
                  years.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
